import React, { useEffect, useState } from "react";

import { Button, Form, Modal } from "react-bootstrap";

import { ArtCentreArtistFragment, ParticipantTypes, RecipientRelationshipInput } from "@api/graphql";
import ParticipantSearch from "@components/account_profile/ParticipantSearch";

import { ParticipantWithSinglePseudonym } from "../ParticipantDropdown";

import styles from "./AddEditNewArtistModal.module.scss";

interface AddEditArtistModalProps {
  show: boolean;
  onClose: () => void;
  onSubmit: (recipientInput: RecipientRelationshipInput) => void;
  EditingArtist: ArtCentreArtistFragment | null;
  participantId: number;
}

const AddEditNewArtistModal: React.FC<AddEditArtistModalProps> = ({
  show,
  onClose,
  onSubmit,
  EditingArtist,
  participantId,
}) => {
  const [selectedSearchedParticipant, setSelectedSearchedParticipant] = useState<ParticipantWithSinglePseudonym | null>(
    null,
  );
  const [startDate, setStartDate] = useState<string | undefined>(undefined);
  const [endDate, setEndDate] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (EditingArtist) {
      setSelectedSearchedParticipant(null);
      setStartDate(EditingArtist.start_date || undefined);
      setEndDate(EditingArtist.end_date || undefined);
    } else {
      setStartDate(undefined);
      setEndDate(undefined);
    }
  }, [EditingArtist, show]);

  const handleSubmit = () => {
    if (selectedSearchedParticipant) {
      const recipientInput: RecipientRelationshipInput = {
        recipient_id: participantId,
        conferrer_id: selectedSearchedParticipant.participant_id,
        root_artist_id: selectedSearchedParticipant.participant_id,
        start_date: startDate,
        end_date: endDate,
        percentage_share: 100,
        is_art_centre_or_cmo: true,
        active_ind: !endDate,
      };
      onSubmit(recipientInput);
    } else if (EditingArtist) {
      const recipientInput: RecipientRelationshipInput = {
        id: EditingArtist.id,
        conferrer_id: EditingArtist.conferrer_id,
        is_art_centre_or_cmo: true,
        percentage_share: 100,
        recipient_id: EditingArtist.recipient_id,
        root_artist_id: EditingArtist.root_artist_id,
        start_date: startDate,
        end_date: endDate,
        active_ind: !endDate,
      };
      onSubmit(recipientInput);
    }
  };

  const handleArtistSelect = (newArtist: ParticipantWithSinglePseudonym | null) => {
    setSelectedSearchedParticipant(newArtist);
  };

  return (
    <Modal show={show} onHide={onClose} centered>
      <div className={styles.modalContainer}>
        <Modal.Header className={styles.modalCloseButton} closeButton />
        <Modal.Title className="mb-4">{EditingArtist ? "Edit Artist" : "Add New Artist"}</Modal.Title>
        <Modal.Body>
          <ParticipantSearch
            passedArtist={EditingArtist || undefined}
            onParticipantSelect={handleArtistSelect}
            disabled={!!EditingArtist}
            variables={{
              filter_by: [
                { field: "participantType", eq: ParticipantTypes.ARTIST },
                { field: "excludeConferrers", ne: "" },
                { field: "participantType", ne: ParticipantTypes.BENEFICIARY },
              ],
            }}
          />

          <Form.Group className={styles.dateSelection}>
            <Form.Label>Start Date</Form.Label>
            <Form.Control type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </Form.Group>

          <Form.Group className={styles.dateSelection}>
            <Form.Label>End Date</Form.Label>
            <Form.Control type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </Form.Group>
        </Modal.Body>

        <Button onClick={handleSubmit} disabled={!EditingArtist && !selectedSearchedParticipant}>
          Submit
        </Button>
      </div>
    </Modal>
  );
};

export default AddEditNewArtistModal;
